import { useState } from "react";
import { Link } from "react-router-dom";
import styles from "./notificationRows.module.css";

// Shared by the notifications panel and the notifications page: how a notification is
// recognised, shortened, coloured and bundled with others of its kind.
const Svg = ({ children }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

export const ICONS = {
  box: <Svg><path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" /><path d="m3.3 7 8.7 5 8.7-5" /><path d="M12 22V12" /></Svg>,
  truck: <Svg><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" /><path d="M15 18H9" /><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.62l-3.48-4.35A1 1 0 0 0 17.52 8H14" /><circle cx="17" cy="18" r="2" /><circle cx="7" cy="18" r="2" /></Svg>,
  chat: <Svg><path d="M7.9 20A9 9 0 1 0 4 16.1L2 22z" /></Svg>,
  check: <Svg><circle cx="12" cy="12" r="9" /><path d="m8.5 12.5 2.5 2.5 4.5-5" /></Svg>,
  card: <Svg><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" /></Svg>,
  wallet: <Svg><path d="M19 7V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" /><path d="M21 8h-5a3 3 0 0 0 0 6h5a1 1 0 0 0 1-1V9a1 1 0 0 0-1-1Z" /></Svg>,
  alert: <Svg><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /><path d="M12 9v4" /><path d="M12 17h.01" /></Svg>,
  bell: <Svg><path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" /></Svg>,
};

// Every kind of notification: how to recognise it, its icon and colour, a short line for one,
// a summary for several, and where it links to.
const KINDS = [
  {
    key: "fulfilled",
    match: /order number\s*#?(\S+)\s+is fulfilled/i,
    icon: "box",
    tone: "violet",
    one: (ref) => `Order #${ref} fulfilled`,
    many: (n) => `${n} orders fulfilled`,
    to: "/customer/orders",
  },
  {
    key: "shipped",
    match: /order number\s*#?(\S+)\s+is shipped/i,
    icon: "truck",
    tone: "info",
    one: (ref) => `Order #${ref} shipped`,
    many: (n) => `${n} orders shipped`,
    to: "/customer/orders/order-tracking",
  },
  {
    key: "dispute",
    match: /replies to your dispute for order number\s*#?(\S+)/i,
    icon: "chat",
    tone: "attention",
    one: (ref) => `New reply on dispute #${ref}`,
    many: (n) => `${n} new replies on disputes`,
    to: "/customer/orders/dispute-list",
  },
  {
    key: "quote",
    match: /sourcing request for (.+?) is completed/i,
    icon: "check",
    tone: "success",
    one: (ref) => `Quote ready: ${ref}`,
    many: (n) => `${n} quotes ready`,
    to: "/customer/products",
    named: true,
  },
  { key: "failed", match: /reject|fail|disapprov/i, icon: "alert", tone: "critical", many: (n) => `${n} things need a look` },
  { key: "topup", match: /top.?up/i, icon: "wallet", tone: "violet", many: (n) => `${n} wallet updates`, to: "/customer/payments" },
  { key: "payment", match: /payment|paid/i, icon: "card", tone: "success", many: (n) => `${n} payment updates`, to: "/customer/payments" },
];
const OTHER = { key: "other", icon: "bell", tone: "neutral", many: (n) => `${n} updates` };

const describe = (n) => {
  const source = `${n.title || ""} ${n.text || ""} ${n.notification_for || ""}`;
  for (const kind of KINDS) {
    const m = source.match(kind.match);
    if (m) {
      const ref = kind.one && m[1] ? m[1].replace(/[.,]$/, "") : null;
      return { kind, ref, line: ref ? kind.one(ref) : n.title || n.text };
    }
  }
  return { kind: OTHER, ref: null, line: n.title || n.text };
};

export const GROUPS = [
  ["today", "Today"],
  ["yesterday", "Yesterday"],
  ["before_that", "Earlier"],
];

const timeAgo = (date) => {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(date).getTime()) / 60000));
  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d`;
  return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short" }).format(new Date(date));
};

// "#7420, #7421 and 8 more", or the product names for quotes.
const refList = (items, named) => {
  const refs = items.filter((i) => i.ref).map((i) => (named ? i.ref : `#${i.ref}`));
  if (refs.length <= 3) return refs.join(", ");
  return `${refs.slice(0, 2).join(", ")} and ${refs.length - 2} more`;
};

// Within one day, notifications of the same kind become one row; the rest stay single.
export function bundleNotifications(items) {
  const rows = [];
  const byKind = new Map();
  items.forEach((n) => {
    const d = { ...describe(n), n };
    const key = d.kind.key;
    if (!byKind.has(key)) {
      const row = { key: `${key}-${n._id}`, kind: d.kind, items: [] };
      byKind.set(key, row);
      rows.push(row);
    }
    byKind.get(key).items.push(d);
  });
  return rows;
}

export function NotificationRow({ row, onClose }) {
  const [open, setOpen] = useState(false);
  const { kind, items } = row;
  const first = items[0];
  const many = items.length > 1;
  const unread = items.some((i) => !i.n.is_read);
  const icon = <span className={`${styles.icon} ${styles[`tone_${kind.tone}`]}`}>{ICONS[kind.icon]}</span>;
  const time = (
    <time className={styles.time} dateTime={first.n.createdAt}>
      {timeAgo(first.n.createdAt)}
    </time>
  );

  if (!many) {
    const body = (
      <>
        {icon}
        <span className={styles.text}>
          <b>{first.line}</b>
          {!first.ref && first.n.title && first.n.text && <span>{first.n.text}</span>}
        </span>
        {time}
      </>
    );
    return (
      <li className={`${styles.row} ${unread ? styles.unread : ""}`}>
        {kind.to ? (
          <Link to={kind.to} className={styles.item} onClick={onClose}>
            {body}
          </Link>
        ) : (
          <div className={styles.item}>{body}</div>
        )}
      </li>
    );
  }

  return (
    <li className={`${styles.row} ${unread ? styles.unread : ""}`}>
      <button type="button" className={styles.item} onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        {icon}
        <span className={styles.text}>
          <b>
            {kind.many(items.length)}
            <svg className={`${styles.chev} ${open ? styles.chevOpen : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="m6 9 6 6 6-6" />
            </svg>
          </b>
          <span>{refList(items, kind.named) || first.line || first.n.title}</span>
        </span>
        {time}
      </button>
      {open && (
        <ul className={styles.sub}>
          {items.map((i) => (
            <li key={i.n._id}>
              {kind.to ? (
                <Link to={kind.to} onClick={onClose}>
                  {i.line}
                </Link>
              ) : (
                <span>{i.line}</span>
              )}
              <time dateTime={i.n.createdAt}>{timeAgo(i.n.createdAt)}</time>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

